import { Injectable, Signal, computed, inject } from '@angular/core';

import type { AugmentedGoal } from 'src/app/domain/goals/types/Goal.model';
import type { Observation } from 'src/app/domain/observations/types/observations.types';
import type { Student, StudentNote } from 'src/app/domain/student/types/Student.model';
import { FirestoreService } from 'src/app/core/services/firestore/firestore';
import { AnalyticsService } from 'src/app/core/services/analytics';
import { AuditLogService } from 'src/app/core/services/audit-log';
import { AccountState } from 'src/app/store';
import { MyGoalsState } from 'src/app/store/my-goals';
import { MyObservationsState } from 'src/app/store/my-observations';
import { MyStudentsState } from 'src/app/store/my-students';
import { RouterState } from 'src/app/store/router';

import type { StudentDataScope, StudentRemovalMode, StudentScopeKind } from './student-data-scope';

/**
 * {@link StudentDataScope} over the signed-in practitioner's own caseload —
 * the Notebook scope. A pass-through to `My*State`; it holds no state, so
 * every read here is the same memoized store signal a component would have
 * injected directly.
 *
 * @security FERPA - handles student PII
 */
@Injectable({ providedIn: 'root' })
export class CaseloadStudentDataScope implements StudentDataScope {
  private readonly studentsState = inject(MyStudentsState);
  private readonly analytics = inject(AnalyticsService);
  private readonly auditLog = inject(AuditLogService);
  private readonly goalsState = inject(MyGoalsState);
  private readonly observationsState = inject(MyObservationsState);
  private readonly accountState = inject(AccountState);
  private readonly routerState = inject(RouterState);
  private readonly firestoreService = inject(FirestoreService);

  readonly kind: StudentScopeKind = 'caseload';

  /**
   * Notebook's removal detaches the student from this practitioner only; the
   * record and every other practitioner's access survive.
   */
  readonly removalMode: StudentRemovalMode = 'remove-from-caseload';

  /** Practitioners may seed an empty disability list, never rewrite one. */
  readonly canEditProtectedFields = false;

  // ── Students ───────────────────────────────────────────────────────────

  readonly students: Signal<Student[]> = this.studentsState.data;
  readonly studentNameMap = this.studentsState.nameMap;

  getStudentById(id: string): Student | null {
    return this.studentsState.getStudentById(id);
  }

  updateStudentFields(studentId: string, fields: Partial<Student>): void {
    const changedFields = Object.keys(fields);
    // Recorded once the write succeeds; the store updates the screen first.
    this.studentsState.updateStudentFields(studentId, fields, () => {
      this.analytics.track('Student Updated', { fields_changed: changedFields });
      this.auditLog.recordDistrict({
        area: 'students',
        action: 'update',
        target: { type: 'student', id: studentId },
        changedFields,
      });
    });
  }

  addNote(studentId: string, content: string): StudentNote {
    return this.studentsState.addNote(studentId, content, () => this.analytics.track('Student Note Added'));
  }

  editNote(studentId: string, noteId: string, content: string): void {
    this.studentsState.editNote(studentId, noteId, content);
  }

  removeNote(studentId: string, noteId: string): void {
    this.studentsState.removeNote(studentId, noteId, () => this.analytics.track('Student Note Deleted'));
  }

  updateAccommodations(studentId: string, accommodations: string[]): void {
    this.studentsState.updateAccommodations(studentId, accommodations);
  }

  updateModifications(studentId: string, modifications: string[]): void {
    this.studentsState.updateModifications(studentId, modifications);
  }

  /**
   * Detach the student from the signed-in practitioner's caseload by dropping
   * the id from their `studentIds`. The student record itself is untouched,
   * and every other practitioner assigned to them keeps their access.
   */
  async removeStudent(studentId: string): Promise<void> {
    const teamId = this.accountState.activeTeam()?.id;
    const userEmail = this.accountState.email();
    if (!teamId || !userEmail) throw new Error('No active team or signed-in user');

    const userDocPath = `partners/${teamId}/users/${userEmail.toLowerCase()}`;
    const userDoc = await this.firestoreService.getDocumentAsync<{ studentIds?: string[] }>(userDocPath);

    if (userDoc?.studentIds?.includes(studentId)) {
      await this.firestoreService.updateDocumentAsync(userDocPath, {
        studentIds: userDoc.studentIds.filter(id => id !== studentId),
        updatedAt: Date.now(),
      });
      this.analytics.track('Student Removed From Caseload');
      this.auditLog.recordDistrict({
        area: 'students',
        action: 'unassign',
        target: { type: 'student', id: studentId },
      });
    }

    this.removeStudentLocally(studentId);
  }

  removeStudentLocally(studentId: string): void {
    this.studentsState.removeStudent(studentId);
  }

  // ── Goals ──────────────────────────────────────────────────────────────

  /** `MyGoalsState.activeGoals` already means `status === 'active'`. */
  readonly activeGoals: Signal<AugmentedGoal[]> = this.goalsState.activeGoals;

  getGoalsByStudentId(studentId: string): AugmentedGoal[] {
    return this.goalsState.getGoalsByStudentId(studentId);
  }

  // ── Observations ───────────────────────────────────────────────────────

  readonly observations: Signal<Observation[]> = this.observationsState.data;
  readonly recentObservations: Signal<Observation[]> = this.observationsState.recentObservations;

  getObservationsByStudentId(studentId: string): Observation[] {
    return this.observationsState.getObservationsByStudentId(studentId);
  }

  // ── Navigation ─────────────────────────────────────────────────────────

  /**
   * A student is reachable through two Notebook lists — `caseload/:studentId`
   * and `students/:studentId` — and the detail page must return to whichever
   * the user actually came through. Reading it off the live URL (rather than
   * in-memory nav history) keeps back correct after a deep link or reload,
   * where there is no previous page to pop.
   */
  private readonly listSegment = computed<'caseload' | 'students'>(() => {
    const path = this.routerState.currentUrl().split('?')[0].split('#')[0];
    return path.startsWith(this.accountState.notebookUrl('students') + '/') ? 'students' : 'caseload';
  });

  readonly canAuthorGoals = true;

  /** The Notebook's routes already require Tools, and compatibility mode has its own surfaces. */
  readonly canAddGoalsAndObservations = true;

  /** Practitioners delete drafts from the goal page, not from the Goals tab. */
  readonly canDeleteGoals = false;

  goalUrl(goalId: string): string {
    return this.accountState.notebookUrl('goal', goalId);
  }

  goalDrafterUrl(studentId: string): string {
    return this.accountState.notebookUrl('goal-drafter', studentId);
  }

  goalCollectUrl(goalId: string): string {
    return this.accountState.notebookUrl('collect', 'goal', goalId);
  }

  progressReportUrl(studentId: string): string {
    return this.accountState.notebookUrl('progress-report', studentId);
  }

  studentListUrl(): string {
    return this.accountState.notebookUrl(this.listSegment());
  }

  studentTabUrl(studentId: string, tab: string): string {
    return this.accountState.notebookUrl(this.listSegment(), studentId, tab);
  }
}
